import { useCallback, useEffect, useMemo, useState } from 'react'
import { getCounselorParentEmailDeliveryLogs } from 'api/parentCommunications'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { parentContactsStore } from 'shared/stores/parentContactsStore'
import {
	CONTACT_TYPE_LABELS,
	LOG_FILTER_STATUSES,
	LOG_STATUSES,
	LOG_STATUS_LABELS,
	NOTIFICATION_BY_CODE,
	formatRecipientsSummary,
	getLogStatusCounts,
	getUniqueRecipientsFromLogs,
	getUniqueStudentsFromLogs,
} from '../../../data/parentCommunicationLogsModel'
import {
	USER_FRIENDLY_ERROR,
	toUserFriendlyError,
} from '../../InlineErrorBanner/InlineErrorBanner'
import { mapParentDeliveryLogs } from '../utils/mapParentDeliveryLogs'

const EMPTY_FILTERS = {
	studentIds: [],
	notificationCodes: [],
	statuses: [],
	recipientIds: [],
	dateFrom: '',
	dateTo: '',
}

const QUICK_STATUS_OPTIONS = [
	{ id: 'all', label: 'All' },
	{ id: LOG_STATUSES.SENT, label: LOG_STATUS_LABELS[LOG_STATUSES.SENT] },
	{ id: LOG_STATUSES.FAILED, label: LOG_STATUS_LABELS[LOG_STATUSES.FAILED] },
]

const mkTextSortOptions = key => [
	{ id: `${key}_asc`, key, dir: 'asc', label: 'A → Z' },
	{ id: `${key}_desc`, key, dir: 'desc', label: 'Z → A' },
]

const mkDateSortOptions = key => [
	{ id: `${key}_asc`, key, dir: 'asc', label: 'Earliest first' },
	{ id: `${key}_desc`, key, dir: 'desc', label: 'Latest first' },
]

export const LOG_SORT_OPTIONS = {
	student: mkTextSortOptions('student'),
	recipients: mkTextSortOptions('recipients'),
	notification: mkTextSortOptions('notification'),
	status: mkTextSortOptions('status'),
	created_at: mkDateSortOptions('created_at'),
}

const compareText = (a, b) =>
	String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' })

const getSortValue = (event, key) => {
	if (key === 'student') return event.student?.name || ''
	if (key === 'recipients') return formatRecipientsSummary(event.recipients).names
	if (key === 'notification') return event.notification?.name || ''
	if (key === 'status') return LOG_STATUS_LABELS[event.status] || event.status || ''
	if (key === 'created_at') {
		const time = new Date(event.created_at).getTime()
		return Number.isNaN(time) ? 0 : time
	}
	return ''
}

const toDayStart = isoDate => {
	if (!isoDate) return null
	const date = new Date(`${isoDate}T00:00:00`)
	return Number.isNaN(date.getTime()) ? null : date
}

const toDayEnd = isoDate => {
	if (!isoDate) return null
	const date = new Date(`${isoDate}T23:59:59.999`)
	return Number.isNaN(date.getTime()) ? null : date
}

const matchesRecipientIds = (recipients, recipientIds) => {
	if (!recipientIds?.length) return true
	return (recipients || []).some(recipient =>
		recipientIds.includes(recipient.id),
	)
}

const matchesDateRange = (createdAt, dateFrom, dateTo) => {
	const created = new Date(createdAt)
	if (Number.isNaN(created.getTime())) return false

	const from = toDayStart(dateFrom)
	const to = toDayEnd(dateTo)
	if (from && created < from) return false
	if (to && created > to) return false
	return true
}

/**
 * Quick filters All/Sent/Failed match event.status exactly.
 * Derived Partial appears under All only.
 */
const matchesQuickStatus = (event, quickStatus) => {
	if (!quickStatus || quickStatus === 'all') return true
	return event.status === quickStatus
}

const matchesDrawerStatuses = (event, statuses) => {
	if (!statuses?.length) return true
	return statuses.includes(event.status)
}

const buildStudentsById = students => {
	const map = new Map()
	;(students || []).forEach(student => {
		const id = String(student?.id || '').trim()
		if (id) map.set(id, student)
	})
	return map
}

const buildContactsById = () => {
	const map = new Map()
	parentContactsStore.contactsByStudentId.forEach(contacts => {
		;(contacts || []).forEach(contact => {
			const id = String(contact?.id || '').trim()
			if (id) map.set(id, contact)
		})
	})
	return map
}

export const useLogs = () => {
	const [events, setEvents] = useState([])
	const [logsLoadStatus, setLogsLoadStatus] = useState('idle')
	const [logsError, setLogsError] = useState(null)

	const [activeGrade, setActiveGrade] = useState('all')
	const [quickStatus, setQuickStatus] = useState('all')
	const [isFilterOpen, setIsFilterOpen] = useState(false)
	const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS)
	const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
	const [expandedRowIds, setExpandedRowIds] = useState([])
	const [sortKey, setSortKey] = useState(null)
	const [sortDir, setSortDir] = useState('asc')
	const [sortLabel, setSortLabel] = useState('')

	const loadLogs = useCallback(async () => {
		setLogsLoadStatus('loading')
		setLogsError(null)

		try {
			const logsResponse = await getCounselorParentEmailDeliveryLogs()
			const apiLogs = Array.isArray(logsResponse?.data) ? logsResponse.data : []

			let studentsById = buildStudentsById(studentsStore.students)
			let contactsById = buildContactsById()

			try {
				await ensureStudentsLoaded()
				studentsById = buildStudentsById(studentsStore.students)
				const studentIds = (studentsStore.students || []).map(s => s.id)
				await parentContactsStore.loadAllContacts({ studentIds })
				contactsById = buildContactsById()
			} catch (enrichmentError) {
				console.error(
					'[Parent logs] enrichment failed; showing API data only',
					enrichmentError,
				)
			}

			const mapped = mapParentDeliveryLogs(apiLogs, {
				studentsById,
				contactsById,
			})

			setEvents(mapped)
			setLogsLoadStatus('success')
		} catch (error) {
			setLogsError(toUserFriendlyError(error, USER_FRIENDLY_ERROR))
			setLogsLoadStatus('error')
			setEvents([])
		}
	}, [])

	useEffect(() => {
		loadLogs()
	}, [loadLogs])

	const dismissLogsError = useCallback(() => {
		setLogsError(null)
	}, [])

	const { allCount, gradeTabs } = useMemo(() => {
		const gradeCounts = {}

		events.forEach(event => {
			const grade = Number(event.student?.gradeNumber)
			if (grade && !Number.isNaN(grade) && grade > 0) {
				gradeCounts[grade] = (gradeCounts[grade] || 0) + 1
			}
		})

		return {
			allCount: events.length,
			gradeTabs: Object.entries(gradeCounts)
				.sort((a, b) => Number(a[0]) - Number(b[0]))
				.map(([grade, count]) => ({
					id: `grade-${grade}`,
					label: `Grade ${grade}`,
					count,
				})),
		}
	}, [events])

	const tabs = useMemo(
		() => [{ id: 'all', label: 'All Students', count: allCount }, ...gradeTabs],
		[allCount, gradeTabs],
	)

	const gradeFilteredEvents = useMemo(() => {
		if (!activeGrade || activeGrade === 'all') return events
		if (!String(activeGrade).startsWith('grade-')) return events

		const gradeNum = Number(String(activeGrade).replace('grade-', ''))
		if (Number.isNaN(gradeNum)) return events

		return events.filter(event => Number(event.student?.gradeNumber) === gradeNum)
	}, [activeGrade, events])

	const statusCounts = useMemo(
		() => getLogStatusCounts(gradeFilteredEvents),
		[gradeFilteredEvents],
	)
	const studentOptions = useMemo(
		() => getUniqueStudentsFromLogs(gradeFilteredEvents),
		[gradeFilteredEvents],
	)

	const filteredEvents = useMemo(() => {
		const next = gradeFilteredEvents.filter(event => {
			if (!matchesQuickStatus(event, quickStatus)) return false
			if (
				appliedFilters.studentIds.length &&
				!appliedFilters.studentIds.includes(event.student.id)
			) {
				return false
			}
			if (
				appliedFilters.notificationCodes.length &&
				!appliedFilters.notificationCodes.includes(event.notification.code)
			) {
				return false
			}
			if (!matchesDrawerStatuses(event, appliedFilters.statuses)) return false
			if (!matchesRecipientIds(event.recipients, appliedFilters.recipientIds)) {
				return false
			}
			if (
				!matchesDateRange(
					event.created_at,
					appliedFilters.dateFrom,
					appliedFilters.dateTo,
				)
			) {
				return false
			}
			return true
		})

		if (!sortKey) return next

		return [...next].sort((a, b) => {
			const left = getSortValue(a, sortKey)
			const right = getSortValue(b, sortKey)
			const result =
				typeof left === 'number' && typeof right === 'number'
					? left - right
					: compareText(left, right)
			return sortDir === 'desc' ? -result : result
		})
	}, [appliedFilters, gradeFilteredEvents, quickStatus, sortDir, sortKey])

	const hasActivePanelFilters = useMemo(() => {
		return (
			appliedFilters.studentIds.length > 0 ||
			appliedFilters.notificationCodes.length > 0 ||
			appliedFilters.statuses.length > 0 ||
			appliedFilters.recipientIds.length > 0 ||
			Boolean(appliedFilters.dateFrom) ||
			Boolean(appliedFilters.dateTo)
		)
	}, [appliedFilters])

	const isEmptyDataset =
		logsLoadStatus === 'success' && events.length === 0
	const isEmptyFiltered = !isEmptyDataset && filteredEvents.length === 0

	const clearSort = useCallback(() => {
		setSortKey(null)
		setSortDir('asc')
		setSortLabel('')
	}, [])

	const setSort = useCallback((key, dir, label) => {
		setSortKey(key)
		setSortDir(dir)
		setSortLabel(label)
	}, [])

	const notificationNameByCode = useMemo(() => {
		const map = { ...NOTIFICATION_BY_CODE }
		events.forEach(event => {
			const code = event?.notification?.code
			if (!code) return
			if (!map[code]) {
				map[code] = {
					code,
					name: event.notification?.name || code,
				}
			}
		})
		return map
	}, [events])

	const filterChips = useMemo(() => {
		const chips = []

		if (appliedFilters.studentIds.length) {
			const labels = appliedFilters.studentIds
				.map(id => studentOptions.find(s => s.id === id)?.name || id)
				.filter(Boolean)
			chips.push({
				id: 'studentIds',
				key: 'studentIds',
				kind: 'filter',
				label: `Student: ${labels.join(', ')}`,
			})
		}

		if (appliedFilters.notificationCodes.length) {
			const labels = appliedFilters.notificationCodes
				.map(
					code =>
						notificationNameByCode[code]?.name || code,
				)
				.filter(Boolean)
			chips.push({
				id: 'notificationCodes',
				key: 'notificationCodes',
				kind: 'filter',
				label: `Notification: ${labels.join(', ')}`,
			})
		}

		if (appliedFilters.statuses.length) {
			const labels = appliedFilters.statuses.map(
				status => LOG_STATUS_LABELS[status] || status,
			)
			chips.push({
				id: 'statuses',
				key: 'statuses',
				kind: 'filter',
				label: `Status: ${labels.join(', ')}`,
			})
		}

		if (appliedFilters.recipientIds.length) {
			const allRecipients = getUniqueRecipientsFromLogs(events)
			const labels = appliedFilters.recipientIds
				.map(id => allRecipients.find(item => item.id === id)?.name || id)
				.filter(Boolean)
			chips.push({
				id: 'recipientIds',
				key: 'recipientIds',
				kind: 'filter',
				label: `Recipient: ${labels.join(', ')}`,
			})
		}

		if (appliedFilters.dateFrom || appliedFilters.dateTo) {
			const from = appliedFilters.dateFrom || '…'
			const to = appliedFilters.dateTo || '…'
			chips.push({
				id: 'dateRange',
				key: 'dateRange',
				kind: 'filter',
				label: `Date: ${from} – ${to}`,
			})
		}

		if (sortKey && sortLabel) {
			chips.push({
				id: 'sort',
				key: 'sort',
				kind: 'sort',
				label: sortLabel,
			})
		}

		return chips
	}, [
		appliedFilters,
		events,
		notificationNameByCode,
		sortKey,
		sortLabel,
		studentOptions,
	])

	const studentFilterOptions = useMemo(
		() =>
			studentOptions.map(student => ({
				value: student.id,
				label: student.name,
				count: gradeFilteredEvents.filter(event => event.student.id === student.id)
					.length,
			})),
		[gradeFilteredEvents, studentOptions],
	)

	const notificationFilterOptions = useMemo(() => {
		const codes = new Set()
		gradeFilteredEvents.forEach(event => {
			if (event?.notification?.code) codes.add(event.notification.code)
		})

		return Array.from(codes)
			.sort((a, b) =>
				String(notificationNameByCode[a]?.name || a).localeCompare(
					String(notificationNameByCode[b]?.name || b),
				),
			)
			.map(code => ({
				value: code,
				label: notificationNameByCode[code]?.name || code,
				count: gradeFilteredEvents.filter(
					event => event.notification.code === code,
				).length,
			}))
	}, [gradeFilteredEvents, notificationNameByCode])

	const recipientFilterOptions = useMemo(() => {
		const recipients = getUniqueRecipientsFromLogs(
			gradeFilteredEvents,
			draftFilters.studentIds,
		)
		return recipients.map(recipient => ({
			value: recipient.id,
			label: recipient.name || recipient.email || recipient.id,
			hint: recipient.email,
			meta: CONTACT_TYPE_LABELS[recipient.type] || recipient.type || '',
			searchText: `${recipient.name || ''} ${recipient.email || ''}`,
			count: gradeFilteredEvents.filter(event =>
				(event.recipients || []).some(item => item.id === recipient.id),
			).length,
		}))
	}, [draftFilters.studentIds, gradeFilteredEvents])

	const statusFilterOptions = useMemo(
		() =>
			LOG_FILTER_STATUSES.map(status => ({
				value: status,
				label: LOG_STATUS_LABELS[status],
				count: statusCounts[status] || 0,
			})),
		[statusCounts],
	)

	const pruneRecipientIds = useCallback(
		(recipientIds, studentIds) => {
			const allowed = new Set(
				getUniqueRecipientsFromLogs(gradeFilteredEvents, studentIds).map(
					item => item.id,
				),
			)
			return (recipientIds || []).filter(id => allowed.has(id))
		},
		[gradeFilteredEvents],
	)

	const onChangeFilterKey = useCallback(
		(key, values) => {
			setDraftFilters(prev => {
				const next = { ...prev, [key]: values }
				if (key === 'studentIds') {
					next.recipientIds = pruneRecipientIds(prev.recipientIds, values)
				}
				return next
			})
		},
		[pruneRecipientIds],
	)

	const onChangeFilterField = useCallback((key, value) => {
		setDraftFilters(prev => ({ ...prev, [key]: value }))
	}, [])

	const applyFilters = useCallback(() => {
		setAppliedFilters(draftFilters)
		setIsFilterOpen(false)
	}, [draftFilters])

	const clearAllPanelFilters = useCallback(() => {
		setDraftFilters(EMPTY_FILTERS)
		setAppliedFilters(EMPTY_FILTERS)
		clearSort()
	}, [clearSort])

	const removeFilterChip = useCallback(
		chip => {
			if (!chip) return
			if (chip.kind === 'sort' || chip.key === 'sort') {
				clearSort()
				return
			}

			const key = chip.key
			if (!key) return

			setAppliedFilters(prev => {
				const next = { ...prev }
				if (key === 'dateRange') {
					next.dateFrom = ''
					next.dateTo = ''
				} else {
					next[key] = []
				}
				return next
			})
			setDraftFilters(prev => {
				const next = { ...prev }
				if (key === 'dateRange') {
					next.dateFrom = ''
					next.dateTo = ''
				} else {
					next[key] = []
				}
				return next
			})
		},
		[clearSort],
	)

	const openFilter = useCallback(() => {
		setDraftFilters(appliedFilters)
		setIsFilterOpen(true)
	}, [appliedFilters])

	const closeFilter = useCallback(() => {
		setDraftFilters(appliedFilters)
		setIsFilterOpen(false)
	}, [appliedFilters])

	const setQuickStatusFilter = useCallback(statusId => {
		setQuickStatus(statusId)
	}, [])

	const toggleExpand = useCallback(rowId => {
		setExpandedRowIds(prev =>
			prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId],
		)
	}, [])

	return {
		events,
		filteredEvents,
		statusCounts,
		tabs,
		activeGrade,
		setActiveGrade,
		quickStatus,
		quickStatusOptions: QUICK_STATUS_OPTIONS,
		setQuickStatusFilter,
		isFilterOpen,
		openFilter,
		closeFilter,
		draftFilters,
		onChangeFilterKey,
		onChangeFilterField,
		applyFilters,
		clearAllPanelFilters,
		filterChips,
		removeFilterChip,
		hasActivePanelFilters,
		isEmptyDataset,
		isEmptyFiltered,
		expandedRowIds,
		toggleExpand,
		studentFilterOptions,
		notificationFilterOptions,
		statusFilterOptions,
		recipientFilterOptions,
		sortKey,
		sortDir,
		setSort,
		logsLoadStatus,
		logsError,
		loadLogs,
		dismissLogsError,
	}
}

export default useLogs
